import React, { useState, useMemo } from 'react';
import { Key, Shield, Plus, Smartphone, Mail, Globe, X, Eye, EyeOff, RefreshCw, ShieldCheck, UserCheck } from 'lucide-react';
import { useSecurityStore, SSOProvider } from '../../../store/securityStore';
import { useAuthStore } from '../../../store/authStore';
import { RowActions } from '../../../components/RowActions';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';

export function AuthenticationSubTab() {
  const { 
    ssoProviders, mfaSettings, passwordPolicy, securityAuditLogs,
    createSSOProvider, updateSSOProvider, deleteSSOProvider, 
    toggleSSOProvider, testSSOConnection, updateMFASettings, 
    resetUserMFA, updatePasswordPolicy, markSectionDirty
  } = useSecurityStore();
  const { user } = useAuthStore();

  const isSuperAdmin = (user?.roles || []).some((r: any) => 
    typeof r === 'string' 
      ? r.toLowerCase().includes('admin') || r.toLowerCase().includes('super')
      : r.code === 'super_admin' || r.name === 'Super Admin'
  );

  const [activeSectionTab, setActiveSectionTab] = useState<'sso' | 'mfa' | 'password' | 'audit'>('sso');

  // SSO Modal State
  const [isSsoModalOpen, setIsSsoModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<SSOProvider | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [ssoForm, setSsoForm] = useState({
    name: '',
    type: 'saml' as 'saml' | 'oidc',
    entityId: '',
    clientSecret: '',
    metadataUrl: '',
    acsUrl: 'https://teamone.app/api/auth/callback/sso',
    defaultRole: 'Employee',
    enforceDomain: '',
    enabled: true,
  });

  // Reset User MFA Modal State
  const [isResetMfaModalOpen, setIsResetMfaModalOpen] = useState(false);
  const [resetUserEmail, setResetUserEmail] = useState('');

  // Password Policy Local State (for live strength preview)
  const [testPassword, setTestPassword] = useState('Password#2026!');
  const [localPwPolicy, setLocalPwPolicy] = useState(passwordPolicy);
  const [pwDirty, setPwDirty] = useState(false);

  // Compute live password strength
  const passwordStrength = useMemo(() => {
    let score = 0;
    const checks = {
      length: testPassword.length >= localPwPolicy.minLength,
      upper: /[A-Z]/.test(testPassword),
      lower: /[a-z]/.test(testPassword),
      number: /[0-9]/.test(testPassword),
      symbol: /[^A-Za-z0-9]/.test(testPassword),
    };

    if (checks.length) score += 25;
    if (checks.upper && checks.lower) score += 25;
    if (checks.number) score += 25;
    if (checks.symbol) score += 25;

    let label = 'Weak';
    let color = 'bg-destructive text-destructive';
    if (score >= 75) {
      label = 'Strong';
      color = 'bg-success text-success';
    } else if (score >= 50) {
      label = 'Medium';
      color = 'bg-warning text-warning';
    }

    return { score, label, color, checks };
  }, [testPassword, localPwPolicy]);

  const handleOpenSsoModal = (provider?: SSOProvider) => {
    if (provider) {
      setEditingProvider(provider);
      setSsoForm({
        name: provider.name,
        type: provider.type,
        entityId: provider.entityId,
        clientSecret: provider.clientSecret || '',
        metadataUrl: provider.metadataUrl,
        acsUrl: provider.acsUrl,
        defaultRole: provider.defaultRole,
        enforceDomain: provider.enforceDomain,
        enabled: provider.enabled,
      });
    } else {
      setEditingProvider(null);
      setSsoForm({
        name: '',
        type: 'saml',
        entityId: '',
        clientSecret: '',
        metadataUrl: '',
        acsUrl: 'https://teamone.app/api/auth/callback/sso',
        defaultRole: 'Employee',
        enforceDomain: '',
        enabled: true,
      });
    }
    setShowSecret(false);
    setIsSsoModalOpen(true);
  };

  const handleSsoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ssoForm.name.trim() || !ssoForm.metadataUrl.trim()) {
      toast.error('Provider name and Metadata URL are required');
      return;
    }

    if (editingProvider) {
      await updateSSOProvider({
        ...editingProvider,
        name: ssoForm.name.trim(),
        type: ssoForm.type,
        entityId: ssoForm.entityId.trim(),
        clientSecret: ssoForm.clientSecret ? '••••••••••••••••••••' : editingProvider.clientSecret,
        metadataUrl: ssoForm.metadataUrl.trim(),
        acsUrl: ssoForm.acsUrl.trim(),
        defaultRole: ssoForm.defaultRole,
        enforceDomain: ssoForm.enforceDomain.trim(),
        enabled: ssoForm.enabled,
        status: ssoForm.enabled ? 'active' : 'inactive',
      });
    } else {
      await createSSOProvider({
        name: ssoForm.name.trim(),
        type: ssoForm.type,
        entityId: ssoForm.entityId.trim(),
        clientSecret: ssoForm.clientSecret ? '••••••••••••••••••••' : undefined,
        metadataUrl: ssoForm.metadataUrl.trim(),
        acsUrl: ssoForm.acsUrl.trim(),
        attributeMapping: {
          email: 'user.email',
          firstName: 'user.firstName',
          lastName: 'user.lastName',
          roles: 'user.groups',
        },
        defaultRole: ssoForm.defaultRole,
        enforceDomain: ssoForm.enforceDomain.trim(),
        enabled: ssoForm.enabled,
        status: ssoForm.enabled ? 'active' : 'inactive',
      });
    }

    setIsSsoModalOpen(false);
  };

  const handleTestConnection = async (id: string) => {
    const res = await testSSOConnection(id);
    if (res.success) {
      toast.success(res.message);
    } else {
      toast.error(res.message);
    }
  };

  const handleSavePwPolicy = async () => {
    await updatePasswordPolicy(localPwPolicy);
    setPwDirty(false);
  };

  const authAuditLogs = useMemo(() => {
    return securityAuditLogs.filter((l) => l.section === 'auth');
  }, [securityAuditLogs]);

  return (
    <div className="space-y-6">
      {/* Sub-tab Navigation */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        {[
          { id: 'sso', label: `SSO Providers (${ssoProviders.length})` },
          { id: 'mfa', label: 'Multi-Factor Auth (MFA)' },
          { id: 'password', label: 'Password & Sessions' },
          { id: 'audit', label: `Auth Audit Trail (${authAuditLogs.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSectionTab(tab.id as any)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeSectionTab === tab.id
                ? 'bg-primary text-primary-foreground shadow-xs'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 1. SSO SECTION */}
      {activeSectionTab === 'sso' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
            <div>
              <h2 className="font-display text-sm font-semibold text-foreground">
                Enterprise Identity Providers (SAML 2.0 &amp; OIDC)
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Delegate enterprise user authentication to corporate IdPs including Okta, Azure AD, and Google Workspace.
              </p>
            </div>
            {isSuperAdmin && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleOpenSsoModal()}
                className="gap-1.5 h-8.5 shrink-0"
              >
                <Plus className="size-3.5" />
                <span>Add IdP Provider</span>
              </Button>
            )}
          </div>

          {/* Providers Cards/Table */}
          <div className="space-y-3">
            {ssoProviders.map((sso) => (
              <div
                key={sso.id}
                className="rounded-2xl border border-border bg-card p-4 sm:p-5 shadow-xs hover:border-border/80 transition-all space-y-3.5"
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex items-start gap-3.5">
                    <div className="size-10 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0 mt-0.5">
                      <Globe className="size-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-xs sm:text-sm text-foreground">{sso.name}</h3>
                        <span className="px-2 py-0.2 rounded-md bg-muted text-foreground border border-border text-3xs font-mono uppercase font-semibold">
                          {sso.type.toUpperCase()}
                        </span>
                        <span
                          className={`px-2 py-0.2 rounded-full font-semibold text-3xs uppercase tracking-wider ${
                            sso.status === 'active'
                              ? 'bg-success/10 text-success-strong border border-success/30'
                              : 'bg-muted text-muted-foreground border border-border'
                          }`}
                        >
                          {sso.status}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 font-mono truncate max-w-lg">
                        {sso.metadataUrl}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestConnection(sso.id)}
                      className="h-8 text-xs gap-1"
                    >
                      <RefreshCw className="size-3" />
                      <span>Test Handshake</span>
                    </Button>

                    <RowActions
                      entityName={sso.name}
                      onEdit={isSuperAdmin ? () => handleOpenSsoModal(sso) : undefined}
                      onToggleStatus={isSuperAdmin ? () => toggleSSOProvider(sso.id) : undefined}
                      isStatusActive={sso.status === 'active'}
                      onDelete={isSuperAdmin ? () => deleteSSOProvider(sso.id) : undefined}
                      onViewDetails={() => handleOpenSsoModal(sso)}
                      readOnly={!isSuperAdmin}
                    />
                  </div>
                </div>

                {/* Metadata details row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2.5 border-t border-border/60 text-xs">
                  <div>
                    <span className="text-3xs uppercase font-semibold text-muted-foreground block">Default Role</span>
                    <span className="font-semibold text-foreground">{sso.defaultRole}</span>
                  </div>
                  <div>
                    <span className="text-3xs uppercase font-semibold text-muted-foreground block">Domain Enforcement</span>
                    <span className="font-semibold text-foreground">{sso.enforceDomain ? `@${sso.enforceDomain}` : 'All Domains'}</span>
                  </div>
                  <div>
                    <span className="text-3xs uppercase font-semibold text-muted-foreground block">Handshake Latency</span>
                    <span className="font-semibold text-foreground">{sso.testLatencyMs ? `${sso.testLatencyMs} ms` : 'Not tested'}</span>
                  </div>
                  <div>
                    <span className="text-3xs uppercase font-semibold text-muted-foreground block">Last Verified</span>
                    <span className="font-semibold text-foreground">
                      {sso.lastTestedAt ? new Date(sso.lastTestedAt).toLocaleDateString() : 'Never'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2. MFA SECTION */}
      {activeSectionTab === 'mfa' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
            <div>
              <h2 className="font-display text-sm font-semibold text-foreground">
                Multi-Factor Authentication (MFA / 2FA) Enforcement
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Enforce second-factor identity verification across administrative and standard roles.
              </p>
            </div>
            {isSuperAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsResetMfaModalOpen(true)}
                className="gap-1.5 h-8.5 shrink-0"
              >
                <UserCheck className="size-3.5" />
                <span>Reset User MFA</span>
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Global Enforce & Roles */}
            <div className="p-5 rounded-2xl border border-border bg-card shadow-xs space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-border">
                <div className="flex items-center gap-2.5">
                  <div className="size-8 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                    <ShieldCheck className="size-4.5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-xs text-foreground">Global MFA Requirement</h3>
                    <p className="text-3xs text-muted-foreground">Require two-factor authentication on sign-in</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  disabled={!isSuperAdmin}
                  checked={mfaSettings.enabled}
                  onChange={(e) => updateMFASettings({ enabled: e.target.checked })}
                  className="size-5 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-2">
                  Enforced Mandatory Roles
                </label>
                <div className="space-y-2">
                  {[
                    { id: 'super_admin', label: 'Super Administrator' },
                    { id: 'hr_admin', label: 'HR Administrator' },
                    { id: 'reporting_manager', label: 'Reporting Manager' },
                    { id: 'employee', label: 'All Employees & Staff' },
                  ].map((role) => {
                    const isChecked = mfaSettings.requiredRoles.includes(role.id);
                    return (
                      <label
                        key={role.id}
                        className="flex items-center gap-2.5 p-2 rounded-lg border border-border hover:bg-muted/20 cursor-pointer text-xs"
                      >
                        <input
                          type="checkbox"
                          disabled={!isSuperAdmin}
                          checked={isChecked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...mfaSettings.requiredRoles, role.id]
                              : mfaSettings.requiredRoles.filter((r) => r !== role.id);
                            updateMFASettings({ requiredRoles: next });
                          }}
                          className="size-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                        <span className="font-medium text-foreground">{role.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Allowed Methods & Grace Period */}
            <div className="p-5 rounded-2xl border border-border bg-card shadow-xs space-y-4">
              <h3 className="font-semibold text-xs text-foreground pb-2 border-b border-border">
                Approved Authentication Factors
              </h3>

              <div className="space-y-2.5">
                {[
                  { key: 'totp', label: 'Authenticator App (TOTP: Google, Authy, 1Password)', icon: <Smartphone className="size-4" /> },
                  { key: 'webAuthn', label: 'Hardware Security Key (FIDO2 / WebAuthn / YubiKey)', icon: <Key className="size-4" /> },
                  { key: 'emailOtp', label: 'Email One-Time Passcode (OTP)', icon: <Mail className="size-4" /> },
                  { key: 'sms', label: 'SMS Text Message OTP (Legacy)', icon: <Smartphone className="size-4" /> },
                ].map((m) => (
                  <label
                    key={m.key}
                    className="flex items-center justify-between p-2.5 rounded-lg border border-border hover:bg-muted/20 cursor-pointer text-xs"
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-muted-foreground">{m.icon}</span>
                      <span className="font-medium text-foreground">{m.label}</span>
                    </div>
                    <input
                      type="checkbox"
                      disabled={!isSuperAdmin}
                      checked={(mfaSettings.methods as any)[m.key]}
                      onChange={(e) => {
                        updateMFASettings({
                          methods: {
                            ...mfaSettings.methods,
                            [m.key]: e.target.checked,
                          },
                        });
                      }}
                      className="size-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                    />
                  </label>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                    Enrollment Grace Period
                  </label>
                  <select
                    disabled={!isSuperAdmin}
                    value={mfaSettings.gracePeriodDays}
                    onChange={(e) => updateMFASettings({ gracePeriodDays: Number(e.target.value) })}
                    className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                  >
                    <option value="0">Immediate (No Grace)</option>
                    <option value="7">7 Days</option>
                    <option value="14">14 Days</option>
                    <option value="30">30 Days</option>
                  </select>
                </div>

                <div>
                  <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                    Remembered Device
                  </label>
                  <select
                    disabled={!isSuperAdmin}
                    value={mfaSettings.rememberDeviceDays}
                    onChange={(e) => updateMFASettings({ rememberDeviceDays: Number(e.target.value) })}
                    className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                  >
                    <option value="0">Never Remember</option>
                    <option value="15">15 Days</option>
                    <option value="30">30 Days</option>
                    <option value="60">60 Days</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3. PASSWORD & SESSIONS SECTION */}
      {activeSectionTab === 'password' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
            <div>
              <h2 className="font-display text-sm font-semibold text-foreground">
                Password Complexity &amp; Session Management
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Define organizational password hygiene, rotation schedules, account lockout thresholds, and session timeouts.
              </p>
            </div>
            {isSuperAdmin && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleSavePwPolicy}
                className="gap-1.5 h-8.5 shrink-0"
              >
                <span>Save Password Policy</span>
              </Button>
            )}
          </div>

          {/* Active Policy Summary Chip */}
          <div className="p-3.5 rounded-xl border border-primary/20 bg-primary/5 text-xs text-foreground flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield className="size-4 text-primary shrink-0" />
              <span>
                <strong>Active Policy:</strong> {localPwPolicy.minLength}+ chars · Upper/Lower/Digits/Special ·{' '}
                {localPwPolicy.expiryDays}-day rotation · Lockout after {localPwPolicy.maxFailedAttempts} attempts ·{' '}
                {localPwPolicy.sessionTimeoutMinutes}m session timeout
              </span>
            </div>
            <span className="px-2 py-0.5 rounded-md bg-primary text-primary-foreground text-3xs font-semibold shrink-0">
              Active Baseline
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Rules Matrix */}
            <div className="p-5 rounded-2xl border border-border bg-card shadow-xs space-y-4">
              <h3 className="font-semibold text-xs text-foreground pb-2 border-b border-border">
                Complexity &amp; Length Rules
              </h3>

              <div>
                <div className="flex justify-between text-xs font-medium mb-1">
                  <span>Minimum Password Length</span>
                  <span className="font-bold text-primary">{localPwPolicy.minLength} Characters</span>
                </div>
                <input
                  type="range"
                  min="8"
                  max="32"
                  disabled={!isSuperAdmin}
                  value={localPwPolicy.minLength}
                  onChange={(e) => {
                    setLocalPwPolicy({ ...localPwPolicy, minLength: Number(e.target.value) });
                    setPwDirty(true);
                  }}
                  className="w-full accent-primary cursor-pointer"
                />
              </div>

              <div className="space-y-2">
                {[
                  { key: 'requireUppercase', label: 'Require at least one uppercase letter (A-Z)' },
                  { key: 'requireLowercase', label: 'Require at least one lowercase letter (a-z)' },
                  { key: 'requireNumbers', label: 'Require at least one numerical digit (0-9)' },
                  { key: 'requireSymbols', label: 'Require at least one symbol or special character (!@#$)' },
                ].map((item) => (
                  <label key={item.key} className="flex items-center gap-2.5 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      disabled={!isSuperAdmin}
                      checked={(localPwPolicy as any)[item.key]}
                      onChange={(e) => {
                        setLocalPwPolicy({ ...localPwPolicy, [item.key]: e.target.checked });
                        setPwDirty(true);
                      }}
                      className="size-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                    />
                    <span className="text-foreground">{item.label}</span>
                  </label>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
                <div>
                  <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                    Password Expiration
                  </label>
                  <select
                    disabled={!isSuperAdmin}
                    value={localPwPolicy.expiryDays}
                    onChange={(e) => {
                      setLocalPwPolicy({ ...localPwPolicy, expiryDays: Number(e.target.value) });
                      setPwDirty(true);
                    }}
                    className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                  >
                    <option value="30">Every 30 Days</option>
                    <option value="60">Every 60 Days</option>
                    <option value="90">Every 90 Days</option>
                    <option value="180">Every 180 Days</option>
                    <option value="0">Never Expire</option>
                  </select>
                </div>

                <div>
                  <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                    Password History
                  </label>
                  <select
                    disabled={!isSuperAdmin}
                    value={localPwPolicy.preventReuseCount}
                    onChange={(e) => {
                      setLocalPwPolicy({ ...localPwPolicy, preventReuseCount: Number(e.target.value) });
                      setPwDirty(true);
                    }}
                    className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                  >
                    <option value="3">No reuse of last 3</option>
                    <option value="5">No reuse of last 5</option>
                    <option value="10">No reuse of last 10</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Lockout, Session & Live Preview */}
            <div className="space-y-4">
              <div className="p-5 rounded-2xl border border-border bg-card shadow-xs space-y-4">
                <h3 className="font-semibold text-xs text-foreground pb-2 border-b border-border">
                  Account Lockout &amp; Session Inactivity
                </h3>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                      Failed Attempts Lockout
                    </label>
                    <select
                      disabled={!isSuperAdmin}
                      value={localPwPolicy.maxFailedAttempts}
                      onChange={(e) => {
                        setLocalPwPolicy({ ...localPwPolicy, maxFailedAttempts: Number(e.target.value) });
                        setPwDirty(true);
                      }}
                      className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                    >
                      <option value="3">3 Attempts</option>
                      <option value="5">5 Attempts</option>
                      <option value="10">10 Attempts</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                      Lockout Duration
                    </label>
                    <select
                      disabled={!isSuperAdmin}
                      value={localPwPolicy.lockoutDurationMinutes}
                      onChange={(e) => {
                        setLocalPwPolicy({ ...localPwPolicy, lockoutDurationMinutes: Number(e.target.value) });
                        setPwDirty(true);
                      }}
                      className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                    >
                      <option value="15">15 Minutes</option>
                      <option value="30">30 Minutes</option>
                      <option value="60">60 Minutes</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                    Session Inactivity Timeout
                  </label>
                  <select
                    disabled={!isSuperAdmin}
                    value={localPwPolicy.sessionTimeoutMinutes}
                    onChange={(e) => {
                      setLocalPwPolicy({ ...localPwPolicy, sessionTimeoutMinutes: Number(e.target.value) });
                      setPwDirty(true);
                    }}
                    className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                  >
                    <option value="15">15 Minutes of inactivity</option>
                    <option value="30">30 Minutes of inactivity</option>
                    <option value="60">1 Hour of inactivity</option>
                    <option value="120">2 Hours of inactivity</option>
                  </select>
                </div>
              </div>

              {/* Interactive Live Password-Strength Preview */}
              <div className="p-4 rounded-2xl border border-border bg-muted/20 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-foreground">Interactive Policy Strength Tester</span>
                  <span className={`text-3xs font-bold px-2 py-0.5 rounded-full ${passwordStrength.color}`}>
                    {passwordStrength.label} ({passwordStrength.score}%)
                  </span>
                </div>

                <input
                  type="text"
                  value={testPassword}
                  onChange={(e) => setTestPassword(e.target.value)}
                  className="w-full h-8.5 px-3 rounded-lg border border-border bg-card text-xs font-mono text-foreground outline-none"
                  placeholder="Type a sample password to test..."
                />

                {/* Progress bar */}
                <div className="w-full bg-border h-1.5 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      passwordStrength.score >= 75
                        ? 'bg-emerald-500'
                        : passwordStrength.score >= 50
                          ? 'bg-amber-500'
                          : 'bg-rose-500'
                    }`}
                    style={{ width: `${passwordStrength.score}%` }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-1 text-3xs text-muted-foreground pt-1">
                  <span className={passwordStrength.checks.length ? 'text-emerald-600 font-semibold' : ''}>
                    ✓ Min {localPwPolicy.minLength} chars
                  </span>
                  <span className={passwordStrength.checks.upper ? 'text-emerald-600 font-semibold' : ''}>
                    ✓ Uppercase letter
                  </span>
                  <span className={passwordStrength.checks.number ? 'text-emerald-600 font-semibold' : ''}>
                    ✓ Number digit
                  </span>
                  <span className={passwordStrength.checks.symbol ? 'text-emerald-600 font-semibold' : ''}>
                    ✓ Symbol character
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 4. AUDIT TRAIL TAB */}
      {activeSectionTab === 'audit' && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-xs">
          <div className="p-4 border-b border-border bg-muted/10 flex items-center justify-between">
            <h3 className="font-semibold text-xs text-foreground">Authentication Governance Audit Trail</h3>
            <span className="text-xs font-semibold text-muted-foreground">{authAuditLogs.length} events</span>
          </div>

          <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
            {authAuditLogs.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-xs">No authentication audit logs</div>
            ) : (
              authAuditLogs.map((log) => (
                <div key={log.id} className="p-3.5 hover:bg-muted/10 text-xs">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-foreground">{log.action}</p>
                      <p className="text-3xs text-muted-foreground mt-0.5">
                        Target: <span className="text-primary font-semibold">{log.target}</span> · Performed by {log.performedBy}
                      </p>
                      {log.newValue && (
                        <p className="text-3xs text-muted-foreground font-mono mt-1 bg-muted/40 p-1 rounded">
                          {log.newValue}
                        </p>
                      )}
                    </div>
                    <span className="text-3xs text-muted-foreground shrink-0">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Add / Edit SSO Drawer (Opens on Right) */}
      {isSsoModalOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-2xs animate-in fade-in"
          onClick={() => setIsSsoModalOpen(false)}
        >
          <div
            className="w-full max-w-lg bg-card border-l border-border h-full flex flex-col justify-between p-6 shadow-2xl animate-in slide-in-from-right duration-200 overflow-y-auto space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <h2 className="font-display text-base font-semibold text-foreground">
                {editingProvider ? 'Edit Identity Provider' : 'Add Identity Provider'}
              </h2>
              <button
                type="button"
                onClick={() => setIsSsoModalOpen(false)}
                className="size-8 rounded-lg hover:bg-muted text-muted-foreground flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSsoSubmit} className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Provider Name</label>
                  <input
                    type="text"
                    value={ssoForm.name}
                    onChange={(e) => setSsoForm({ ...ssoForm, name: e.target.value })}
                    className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary"
                    placeholder="Okta Corporate"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Protocol Type</label>
                  <select
                    value={ssoForm.type}
                    onChange={(e) => setSsoForm({ ...ssoForm, type: e.target.value as any })}
                    className="w-full h-8.5 px-2.5 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary cursor-pointer"
                  >
                    <option value="saml">SAML 2.0</option>
                    <option value="oidc">OpenID Connect (OIDC)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Entity ID / Client ID</label>
                <input
                  type="text"
                  value={ssoForm.entityId}
                  onChange={(e) => setSsoForm({ ...ssoForm, entityId: e.target.value })}
                  className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary font-mono"
                  placeholder="https://identity.company.com/entity"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Client Secret (Masked)</label>
                <div className="relative">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={ssoForm.clientSecret}
                    onChange={(e) => setSsoForm({ ...ssoForm, clientSecret: e.target.value })}
                    className="w-full h-8.5 pl-3 pr-8 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary font-mono"
                    placeholder="••••••••••••••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                  >
                    {showSecret ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">IdP Metadata URL</label>
                <input
                  type="url"
                  value={ssoForm.metadataUrl}
                  onChange={(e) => setSsoForm({ ...ssoForm, metadataUrl: e.target.value })}
                  className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary font-mono"
                  placeholder="https://identity.company.com/metadata.xml"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">ACS / Redirect Callback URL</label>
                <input
                  type="text"
                  value={ssoForm.acsUrl}
                  onChange={(e) => setSsoForm({ ...ssoForm, acsUrl: e.target.value })}
                  className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Default Provisioned Role</label>
                  <select
                    value={ssoForm.defaultRole}
                    onChange={(e) => setSsoForm({ ...ssoForm, defaultRole: e.target.value })}
                    className="w-full h-8.5 px-2.5 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary cursor-pointer"
                  >
                    <option value="Employee">Employee</option>
                    <option value="Reporting Manager">Reporting Manager</option>
                    <option value="Support Agent">Support Agent</option>
                    <option value="HR Admin">HR Admin</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Enforce Domain Login</label>
                  <input
                    type="text"
                    value={ssoForm.enforceDomain}
                    onChange={(e) => setSsoForm({ ...ssoForm, enforceDomain: e.target.value })}
                    className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary"
                    placeholder="company.com"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="sso-enabled"
                  checked={ssoForm.enabled}
                  onChange={(e) => setSsoForm({ ...ssoForm, enabled: e.target.checked })}
                  className="size-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                />
                <label htmlFor="sso-enabled" className="text-xs font-medium text-foreground cursor-pointer">
                  Enable and activate this Identity Provider for authentication
                </label>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-border">
                <Button variant="outline" size="sm" type="button" onClick={() => setIsSsoModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  {editingProvider ? 'Update Provider' : 'Create Provider'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset User MFA Drawer (Opens on Right) */}
      {isResetMfaModalOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-2xs animate-in fade-in"
          onClick={() => setIsResetMfaModalOpen(false)}
        >
          <div
            className="w-full max-w-md bg-card border-l border-border h-full flex flex-col justify-between p-6 shadow-2xl animate-in slide-in-from-right duration-200 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <h2 className="font-display text-base font-semibold text-foreground">Reset User MFA Credentials</h2>
              <button
                type="button"
                onClick={() => setIsResetMfaModalOpen(false)}
                className="size-8 rounded-lg hover:bg-muted text-muted-foreground flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              Resetting a user’s MFA will revoke all enrolled authenticator keys, WebAuthn devices, and backup codes. The user will be required to re-enroll on their next login.
            </p>

            <div>
              <label className="text-xs font-semibold text-foreground block mb-1">User Email Address</label>
              <input
                type="email"
                value={resetUserEmail}
                onChange={(e) => setResetUserEmail(e.target.value)}
                placeholder="user@company.com"
                className="w-full h-9 px-3 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary"
                required
              />
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <Button variant="outline" size="sm" onClick={() => setIsResetMfaModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  if (!resetUserEmail.trim()) {
                    toast.error('Email is required');
                    return;
                  }
                  resetUserMFA('manual', resetUserEmail.trim());
                  setIsResetMfaModalOpen(false);
                  setResetUserEmail('');
                }}
              >
                Confirm Reset MFA
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
